import { Alert, CircularProgress, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { IdAndFaceValidation, IdValidation } from 'idmission-web-sdk';
import { memo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router';

import { ProgressDots } from '@/components/DataDisplay/Indicator/ProgressDots';
import { Icon } from '@/components/Icon/Icon';
import { IconCell } from '@/components/Icon/IconCell';
import { ErrorMessage } from '@/components/Idmission/ErrorMessage';
import { IdmButton } from '@/components/Idmission/IdmButton.styled';
import { IdmCard } from '@/components/Idmission/IdmCard.styled';
import {
  IdmClassNames,
  IdmStylesMobile,
} from '@/components/Idmission/Idmission.styled';
import { RejectedMessage } from '@/components/Idmission/RejectedMessage';
import { FullPageLoaderOverlay } from '@/components/Layout/FullPageLoader';
import { Screen } from '@/components/Layout/Screen';

import { ROUTE } from '@/constants/routes';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';
import { useIdmissionVerbiage } from '@/hooks/verification/useIdmissionVerbiage';
import { useIdmissionVerification } from '@/hooks/verification/useIdmissionVerification';

import { selectLoggedInUser } from '@/redux/auth/selectors';

import { PROGRESS_STEPS } from '@/utils/progressSteps';
import { renderHTMLText } from '@/utils/string';
import {
  checkUserEkycStatus,
  isExistingCustomer,
  isRemediationCustomer,
} from '@/utils/user';

import {
  CardWrapper,
  ContentWrapper,
} from './IdmissionIDValidationMobile.styled';

export const IdmissionIDValidationMobile: React.FC = memo(() => {
  const user = useAppSelector(selectLoggedInUser);
  const { isSelfieChecked, isLivenessVideoRecorded } =
    checkUserEkycStatus(user);
  const [isVerificationStarted, setIsVerificationStarted] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { t } = useTranslate();
  const { verbiage } = useIdmissionVerbiage();

  const onCompleted = () => {
    if (isExistingCustomer(user) || isRemediationCustomer(user)) {
      navigate(ROUTE.MORE_INFORMATION);
    } else {
      navigate(ROUTE.BASIC_INFO);
    }
  };

  const {
    sessionId,
    error,
    isIDRejected,
    isSelfieRejected,
    isLivenessRejected,
    acknowledgeRejection,
    isProcessing,
    isCompleted,
    shouldValidateFace,
    idCaptureRequirement,
    startVerification,
    handleBeforeLivenessCheck,
    handleComplete,
    handleAcknowledgeRejection,
    resetVerification,
    handleOnRequestFailure,
  } = useIdmissionVerification({
    type: 'id',
    autoInit: false,
    isSelfieChecked,
    isLivenessVideoRecorded,
    onCompleted,
  });

  const handleStartVerification = useCallback(async () => {
    try {
      setIsVerificationStarted(true);
      setIsLoading(true);
      await startVerification();
    } catch (err) {
      console.error('Failed to start verification:', err);
    } finally {
      setIsLoading(false);
    }
  }, [startVerification]);

  const handleOnExit = useCallback(() => {
    resetVerification();
    setIsVerificationStarted(false);
  }, [resetVerification]);

  const renderVerificationContent = () => {
    if (!sessionId || isProcessing || isCompleted) {
      return (
        <FullPageLoaderOverlay
          bgColor={(theme: Theme) => theme.palette.background.paper}
        />
      );
    }

    if (error) {
      return <ErrorMessage error={error} onAcknowledge={handleOnExit} />;
    }

    return (
      <IdmStylesMobile>
        {shouldValidateFace ? (
          <IdAndFaceValidation
            sessionId={sessionId}
            idCaptureRequirement={idCaptureRequirement}
            onBeforeLivenessCheck={handleBeforeLivenessCheck}
            onComplete={handleComplete}
            onExitCapture={handleOnExit}
            onExitAfterFailure={handleOnExit}
            onRequestFailure={handleOnRequestFailure}
            idCaptureAllowUploadingDocumentsFromStorage={false}
            classNames={IdmClassNames}
            verbiage={verbiage}
          />
        ) : (
          <IdValidation
            sessionId={sessionId}
            idCaptureRequirement={idCaptureRequirement}
            onComplete={handleComplete}
            onExitCapture={handleOnExit}
            onRequestFailure={handleOnRequestFailure}
            allowUploadingDocumentsFromStorage={false}
            classNames={IdmClassNames.idCapture}
            verbiage={verbiage.idCapture}
          />
        )}
      </IdmStylesMobile>
    );
  };

  if (
    (isIDRejected || isSelfieRejected || isLivenessRejected) &&
    !acknowledgeRejection
  ) {
    return (
      <RejectedMessage
        isIDRejected={isIDRejected}
        isSelfieRejected={isSelfieRejected}
        isLivenessRejected={isLivenessRejected}
        onAcknowledge={handleAcknowledgeRejection}
      />
    );
  }

  if (isVerificationStarted) {
    return renderVerificationContent();
  }

  return (
    <Screen
      header={{
        centerSlot: (
          <ProgressDots
            {...PROGRESS_STEPS.livenessVerificationStep}
            activeColor={(theme: Theme) => theme.palette.primaries[400]}
            inactiveColor={(theme: Theme) => theme.palette.grey[300]}
          />
        ),
      }}
      bgColor={(theme: Theme) => theme.palette.grey[50]}
    >
      <ContentWrapper>
        <Typography variant="h3" textAlign="center">
          {t('verification.idmission.title')}
        </Typography>

        <Typography variant="body2" color="text.secondary">
          {renderHTMLText(
            t(
              `verification.idmission.gettingStartedInstruction.${idCaptureRequirement}`,
            ),
          )}
        </Typography>

        {error && (
          <Alert
            icon={<Icon name="alert" />}
            color="error"
            variant="standard"
            sx={{ width: '100%' }}
          >
            {error}
          </Alert>
        )}

        <CardWrapper>
          <IdmCard>
            <IconCell
              name="camera"
              color={(theme: Theme) => theme.palette.common.white}
              bgColor={(theme: Theme) => theme.palette.primaries[500]}
              size={80}
              iconSize={40}
            />
            <IdmButton
              onClick={handleStartVerification}
              variant="contained"
              disabled={isLoading}
              size="large"
              fullWidth
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t('verification.idmission.idCapture.captureWithCameraText')
              )}
            </IdmButton>
          </IdmCard>
        </CardWrapper>
      </ContentWrapper>
    </Screen>
  );
});
